import { ISliderProps as FISliderProps, ISliderStyles, Slider as FSlider } from '@fluentui/react';
import { forwardRef, ReactNode, Ref, useCallback, useEffect, useRef, useState } from 'react';

import { IComponent, IStyleFunctionOrObject } from '../../models/components.models';
import { useClassNames, useThemeStyles } from '../../utils/hooks';
import {
  ISliderContainerClassNames,
  ISliderContainerStyles,
  sliderContainerStylesDefault,
  sliderStylesDefault,
} from './Slider.styles';

export interface ISliderProps extends Omit<FISliderProps, 'styles'>, IComponent<ISliderStyles> {
  name?: string;
  sliderContainerStyles?:
    | IStyleFunctionOrObject<ISliderContainerStyles>
    | IStyleFunctionOrObject<ISliderContainerStyles>[];
  onRenderLabel?: (props?: ISliderProps) => ReactNode;
}

export const Slider = forwardRef((props: ISliderProps, ref: Ref<HTMLDivElement>) => {
  const { styles, sliderContainerStyles, name, onRenderLabel, ..._fProps } = props;
  const { value, defaultValue, onChange, onChanged, max = 10, min = 0, disabled, showValue, className } = props;
  const [val, setVal] = useState(defaultValue !== undefined ? defaultValue : value);
  const inputRef = useRef<HTMLInputElement>(null);
  const classNames = useClassNames<ISliderContainerStyles, ISliderContainerClassNames>(
    'SliderContainer',
    sliderContainerStylesDefault,
    sliderContainerStyles,
    className
  );
  const sliderOnChange = useCallback(
    (value: number, range: [number, number], e: any) => {
      setVal(value);
      const inputEl = inputRef.current;
      if (inputEl) {
        inputEl.value = String(value);
      }
      if (onChange) {
        onChange(value, range, { ...e, target: inputEl });
      }
    },
    [onChange]
  );
  const sliderOnChanged = useCallback(
    (e: any, value: number, range: [number, number]) => {
      setVal(value);
      const inputEl = inputRef.current;
      if (inputEl) {
        inputEl.value = String(value);
      }
      if (onChanged) {
        onChanged({ ...e, target: inputEl }, value, range);
      }
    },
    [onChanged]
  );
  const fProps: FISliderProps = {
    ..._fProps,
    defaultValue: undefined,
    showValue: !!showValue,
    min,
    max,
    styles: useThemeStyles<Partial<ISliderStyles>>(sliderStylesDefault, styles),
    value: val,
    onChange: sliderOnChange as any,
    onChanged: sliderOnChanged as any,
  };

  const textOnBlur = useCallback(
    (e: any) => {
      let textValue = e.target.value.trim() === '' ? min : +e.target.value;
      if (textValue > max) {
        textValue = max;
      }
      if (textValue < min) {
        textValue = min;
      }
      setVal(textValue);
      e.target.value = textValue;
      if (onChanged) {
        onChanged(e, textValue);
      }
    },
    [onChanged, min, max]
  );
  const inputProps = {
    type: 'number',
    name,
    disabled,
    onBlur: textOnBlur,
  };
  const customLabel = onRenderLabel ? onRenderLabel(props) : undefined;

  useEffect(() => {
    // initialize the input value
    if (inputRef.current) {
      inputRef.current.value = `${val}`;
    }
  }, []);

  return (
    <div ref={ref} className={classNames.root} data-test={props['data-test'] || 'slider'}>
      <div className={classNames.textField}>
        <input {...inputProps} className={classNames.input} ref={inputRef} />
      </div>
      {customLabel && (
        <div className={classNames.customLabelContainer}>
          <div className={classNames.customLabel}>{customLabel}</div>
        </div>
      )}
      <FSlider {...fProps} label={customLabel ? '' : fProps.label} />
      <div className={classNames.minMaxLabel}>
        <div className={classNames.minLabel}>{min}</div>
        <div className={classNames.maxLabel}>{max}</div>
      </div>
    </div>
  );
});
